import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Progress } from 'tea-component';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import { DBO_V } from '@op/utils/format';
import { getQOQText } from '@op/utils/time';
import { IKPIValue, IKPILinkTime } from '../../pages/CreativeTrends/types';
import './KPICell.scss';
import useIntls from '@opm/hooks/useIntls';

export interface IKPICellProps {
  /** 指标名称（例：Impressions / Creatives / Avg. Days Active） */
  label: string;
  /** 完整 KPI 数据（含 DoD）— 优先使用 */
  kpiData?: IKPIValue;
  /** 简单数值（无 DoD 场景，例：Avg. Days Active） */
  value?: number | string | null;
  /** 数值格式化函数（默认 DBO_V.reviews） */
  formatValue?: (v: number) => string | number;
  /**
   * 当前 filter（从中提取 date_type/start_time/end_time 计算 DoD/WoW/MoM 文案）
   * 不传则 DoD 标签不显示
   */
  filter?: any;
  /**
   * 全量 max 值（list.md `max` 字段，用于进度条归一化）
   * 传入后渲染进度条（value/max）；不传则不渲染
   */
  max?: number;
  /** label 额外类名 */
  className?: string;
  /** 数值字号风格：`sm` 用于 Card（18px），`lg` 用于详情页（24px Madera） */
  size?: 'sm' | 'lg';
}

/**
 * 解析 link_time 为 [old区间, new区间] 两个 [start, end] 元组
 *  - 对象（list.md 新结构）→ [[old_start, old_end], [new_start, new_end]]
 *  - 字符串（detail.md 旧结构）→ [[link_time], []]（仅 oldTime 单点）
 *  - 缺省/空 → [[], []]
 */
export function parseLinkTime(
  link_time: string | IKPILinkTime | undefined,
): { oldTime: Array<string | undefined>; newTime: Array<string | undefined>; } {
  if (link_time && typeof link_time === 'object') {
    return {
      oldTime: [link_time.old_start_date, link_time.old_end_date],
      newTime: [link_time.new_start_date, link_time.new_end_date],
    };
  }
  return { oldTime: [], newTime: [] };
}

/**
 * KPI 单元格（TrendCard + TrendInfo 共用，column 布局）
 *
 * 布局：label → value → progress → DoD
 *
 * 渲染规则：
 *  - value 优先 kpiData.value → value；为 number 时 formatValue 格式化，其它原样
 *  - 传入 max → 渲染进度条（value/max 归一化）
 *  - kpiData 含 value + old_value（≠0） → 渲染 DoD 行
 *    （PercentageItem + 可选 DoD/WoW/MoM 标签 + RingRatioBubble hover 对比气泡）
 *
 * Table 单元格场景（inline 布局 + 进度条）请使用 `ListView/components/MetricsCell`。
 */
export default function KPICell({
  label,
  kpiData,
  value,
  formatValue = DBO_V.reviews,
  filter,
  max,
  className,
  size = 'sm',
}: IKPICellProps) {
  const { intl } = useIntls();
  // DoD / WoW / MoM 动态文案（由 filter 的 date_type 决定）
  const dodText = useMemo(() => {
    if (!filter) return '';
    const { date_type, start_time, end_time } = filter;
    if (!date_type || !start_time || !end_time) return '';
    const { text } = getQOQText(date_type, start_time, end_time) || {};
    return text || '';
  }, [filter]);

  // 当前展示值（优先 kpiData.value → value）
  const displayValue = kpiData?.value ?? value;
  // 环比百分比：统一用 DBO_V.qoq（= getRationPercentValue），内部已兜底 0/Infinity/负值
  const newValue = kpiData?.value ?? 0;
  const oldValue = kpiData?.old_value ?? 0;
  const ratio = DBO_V.qoq(newValue, oldValue);

  const { oldTime, newTime } = useMemo(
    () => parseLinkTime(kpiData?.link_time),
    [kpiData?.link_time],
  );

  // 进度条归一化比例（0 ~ 100）
  const progressPercent = useMemo(() => {
    if (max == null || max <= 0) return null;
    const numericValue =
      typeof displayValue === 'number' ? displayValue : Number(displayValue);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return 0;
    return Math.min(100, Math.max(0, (numericValue / max) * 100));
  }, [displayValue, max]);

  const formattedValue =
    displayValue == null || displayValue === '' || displayValue === -1
      ? '-'
      : typeof displayValue === 'number'
        ? formatValue(displayValue)
        : displayValue;

  return (
    <div className={classNames('dbom-ct-kpi-cell', className)}>
      <div className="dbom-ct-kpi-cell__label">{label}</div>
      <div
        className={classNames('dbom-ct-kpi-cell__value', {
          'dbom-ct-kpi-cell__value--lg': size === 'lg',
        })}
      >
        {formattedValue}
      </div>
      {progressPercent != null && (
        <Progress
          className="dbom-ct-kpi-cell__progress"
          percent={progressPercent}
          strokeWidth={4}
          theme="default"
        />
      )}
      <RingRatioBubble
        oldTime={oldTime}
        newTime={newTime}
        oldValue={kpiData?.old_value ?? 0}
        newValue={kpiData?.value ?? 0}
        formateValue={formatValue}
      >
        <div className="dbom-ct-kpi-cell__dod">
          {dodText && (
            <span className="dbom-ct-kpi-cell__dod-label">{intl(dodText)}</span>
          )}
          <PercentageItem percentage={ratio} />
        </div>
      </RingRatioBubble>
    </div>
  );
}
